import Column from '@app/components/layout/Column'
import { type ReactNode } from 'react'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { TabsGroup } from '@app/components/core/tabs/Tabs'
import { match } from 'ts-pattern'
import { useParamState } from '@app/util/hooks/useParamState'
import { useQuery } from '@based/sdk/react'
import { Overview } from './tabContent/Overview'
import { Initiatives } from './tabContent/Initiatives'
import { Current } from './tabContent/Current'
import { Thread } from '@app/components/blocks/thread/Thread'
import { Locations } from './tabContent/Locations'
import { StepByStep } from './tabContent/StepByStep'
import { DevelopmentAreas } from './tabContent/DevelopmentAreas'
import { Subsidies } from './tabContent/Subsidies'
import { RegionViewModel } from 'functions/app/geography/view/region'
import { regionsTabs, regionsTabsQueryParamName } from '../constants'
import { KnowledgeHubModulesSection } from '@app/components/blocks/KnowledgeHubModulesSection'
import { isFeatureEnabled, isFeatureAvailable } from '@app/util/featureToggle'
import { RegionalServiceProvidersGrid } from './tabContent/RegionalServiceProvidersGrid'
import { ServiceProviderListRegionalModel } from 'functions/app/service-provider/list-regional'

type Tab = keyof typeof regionsTabs
const regionTabKeys: Tab[] = Object.values(regionsTabs)

const isTabAvailable = (region: RegionViewModel, tab: Tab): boolean => {
  return match(tab)
    .with(regionsTabs.overview, () => true)
    .with(regionsTabs.discussion, () => true)
    .with(regionsTabs.initiatives, () => true)
    .with(regionsTabs.locations, () => true)
    .with(regionsTabs.knowledgeHub, (t) =>
      isFeatureAvailable(region.featureToggles, t, [
        region.knowledgeHubModules,
      ]),
    )
    .with(regionsTabs.current, (t) =>
      isFeatureAvailable(region.featureToggles, t, [
        region.stories,
        region.news,
        region.blogs,
      ]),
    )
    .with(
      regionsTabs.experts,
      (matchedTab) =>
        (region.serviceProviders?.count ?? 0) > 0 &&
        isFeatureEnabled(region.featureToggles, matchedTab),
    )
    .with(regionsTabs.stepByStep, (t) =>
      isFeatureAvailable(region.featureToggles, t, [region.stepByStepPdf]),
    )
    .otherwise((matchedTab) =>
      isFeatureEnabled(region.featureToggles, matchedTab),
    )
}

export const RegionTabContent = ({ region }: { region: RegionViewModel }) => {
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.regionPage
  const [tab, setTab] = useParamState(regionsTabs, regionsTabsQueryParamName)

  const expertsFeatureEnabled = isFeatureEnabled(
    region.featureToggles,
    regionsTabs.experts,
  )
  const regionalExpertsCountQuery = useQuery(
    expertsFeatureEnabled ? 'list-regional-service-providers' : null,
    {
      regionId: region.id,
      count: true,
    },
  )
  const expertsCount = regionalExpertsCountQuery.data?.count ?? 0
  const showExpertsTab = expertsFeatureEnabled && expertsCount > 0

  const regionalExpertsListQuery = useQuery(
    tab === regionsTabs.experts && showExpertsTab
      ? 'list-regional-service-providers'
      : null,
    {
      regionId: region.id,
    },
  )
  const regionalExperts: ServiceProviderListRegionalModel[] =
    regionalExpertsListQuery.data !== undefined
      ? (regionalExpertsListQuery.data as ServiceProviderListRegionalModel[])
      : []

  const getTabLabel = (tabKey: Tab): string => translations[tabKey]

  return (
    <Column alignItems="stretch" gap="2rem">
      <TabsGroup
        selectedTab={tab}
        onSelectTab={setTab}
        tabs={regionTabKeys.map((key) => ({
          value: key,
          label: getTabLabel(key),
          isVisible:
            key === regionsTabs.experts
              ? showExpertsTab
              : isTabAvailable(region, key),
        }))}
      />
      {match(tab)
        .with(regionsTabs.overview, () => (
          <Overview key="overview" entity={region} />
        ))
        .with(regionsTabs.discussion, () => (
          <Thread key="thread" threadId={region.thread?.id} />
        ))
        .with(regionsTabs.initiatives, () => (
          <Initiatives key="initiatives" entity={region} />
        ))
        .with(regionsTabs.locations, () => (
          <Locations key="locations" entity={region} />
        ))
        .with(regionsTabs.knowledgeHub, (t) =>
          isTabAvailable(region, t) ? (
            <KnowledgeHubModulesSection
              key="knowledgeHub"
              modules={region.knowledgeHubModules ?? []}
            />
          ) : null,
        )
        .with(regionsTabs.experts, () =>
          showExpertsTab ? (
            <RegionalServiceProvidersGrid
              key="experts"
              serviceProviders={regionalExperts}
            />
          ) : null,
        )
        .with(regionsTabs.current, () => (
          <Current key="current" region={region} />
        ))
        .with(regionsTabs.developmentAreas, () => (
          <DevelopmentAreas key="developmentAreas" region={region} />
        ))
        .with(regionsTabs.stepByStep, (t) =>
          isTabAvailable(region, t) ? (
            <StepByStep key="stepByStep" region={region} />
          ) : null,
        )
        .with(regionsTabs.subsidies, () => (
          <Subsidies key="subsidies" region={region} />
        ))
        .with(regionsTabs.municipalities, (): ReactNode => null)
        .exhaustive()}
    </Column>
  )
}
